import { useEffect, useRef, useState } from "react";
import { playStreamedVoice, type StreamedVoice } from "../audio/stream-voice";
import type { IrisMode } from "./CapricaIris";
import { icebreakerCopy, photoCopy } from "../iris/maker-language";
import { PhotoOffer, type PhotoChoice } from "./PhotoOffer";
import type { CastOption, CastPick } from "./CastPicker";
import { useHandsFree } from "./useHandsFree";
import { VoiceInput, type VoiceState } from "./VoiceInput";
import "./intake-talk.css";
import { subtitleCopy } from "../iris/maker-language";
import { subtitleView } from "./subtitle-view";
import { AnswerLine } from "./subtitles/AnswerLine";
import { ChoiceSheet } from "./subtitles/ChoiceSheet";
import { IrisPresence } from "./subtitles/IrisPresence";
import { ReadBack } from "./subtitles/ReadBack";
import { SubtitleStage } from "./subtitles/SubtitleStage";
import { useIntakeSource } from "./intake-source-store";

export interface TalkMessage {
  role: "maker" | "caprica";
  text: string;
  /** A pasted lyric sheet is shown as a receipt, never echoed in full. */
  note?: boolean;
  /** Three directions Iris read from the song; `chosen` marks the maker's pick. */
  concepts?: TalkConcept[];
  chosen?: number;
  /** Portraits Iris shows beside this reply (or the photo upload); `picked` once the maker chose. */
  offer?: "cast" | "wardrobe" | "photo_session";
  picked?: boolean;
  /** Tappable answers under this question; gone once the maker answered. */
  quickReplies?: { options: string[]; multi: boolean; extras?: { id: string; label: string }[] };
  answered?: boolean;
  /** Iris could not answer: the subtitle shows it in the attention colour, with "Try again". */
  error?: boolean;
}
export interface TalkConcept {
  name: string;
  logline: string;
  why: string;
  mood: string;
  world: string;
  places: { name: string; look: string; story_moment: string }[];
  movement: string;
  camera: string;
  cast_idea: string;
  story: string;
  /** Iris's recommended production setup for this direction, and why in one sentence. */
  setup?: "best" | "good" | "economy";
  setup_why?: string;
}
export interface TalkLocation {
  name: string;
  look: string;
  story_moment: string;
}
export interface TalkNewCharacter {
  name: string;
  description: string;
  role: string;
}
export interface TalkWardrobe {
  name: string;
  /** A saved, approved look by its exact name. */
  look: string;
  outfit: string;
  hair?: string;
}
export interface TalkCompanion {
  name: string;
  kind: "pet" | "prop";
  description: string;
  owner: string;
  story_moment: string;
  /** Already in the maker's approved library. */
  existing: boolean;
}
export interface TalkView {
  phase: "talk";
  messages: TalkMessage[];
  thinking: boolean;
  song: string;
  lyrics: string;
  locations: TalkLocation[];
  newCast: TalkNewCharacter[];
  wardrobe?: TalkWardrobe[];
  companions?: TalkCompanion[];
  missing: string[];
  ready: boolean;
  warnings: string[];
  castOptions?: CastOption[];
  castPick?: CastPick;
  /** Shown only when Iris remembers the maker's taste (after their yes). */
  tasteLabel?: string;
  /** Sing with your photo is offered once the song is in (the server has the avatar service). */
  photoAvailable?: boolean;
  /** The chosen photo's file name, once the maker picked one with consent. */
  photoChosen?: string;
  /** The decision board is beside the chat: the chat then shows no copies of what the board shows. */
  board?: boolean;
  /** Chat only: how far the story is and the estimate, in one quiet line. */
  progress?: { percent: number; estimate: string };
}
export interface TalkActions {
  send: (text: string) => void;
  addSong: () => void;
  write: () => void;
  guided: () => void;
  /** Back from the step-by-step questions to the free conversation. */
  resume: () => void;
  chooseConcept?: (messageIndex: number, conceptIndex: number) => void;
  pickCast?: (messageIndex: number, pick: CastPick, mode: "cast" | "wardrobe") => void;
  moreConcepts?: () => void;
  /** A fixed chip (skip, show the directions, remember or forget the taste). */
  quickExtra?: (messageIndex: number, id: string) => void;
  forgetTaste?: () => void;
  /** Sing with your photo: a consented photo is ready to travel with the production. */
  photoReady?: (choice: PhotoChoice) => void;
  /** Look at the story panel for a moment while still chatting. */
  peek?: () => void;
  /** Iris could not answer: send the maker's last line again. */
  retry?: () => void;
  /** Bring the cast portraits back after an offer answered in words. */
  castAgain?: () => void;
}

const MISSING_LABELS: Record<string, string> = {
  audio: "the song file",
  artist: "the artist",
  title: "the song title",
  format: "the film type",
  story: "what happens in the film",
  lyrics: "the lyrics, or that it's instrumental",
  cast: "who is in the film",
  places: "where it happens",
  movement: "how people move",
  camera: "how the camera feels",
  text: "whether words appear on screen",
  wardrobe: "what the cast wears",
};

/**
 * A lyric sheet looks like many short lines. It is kept out of the conversation so the
 * words go straight into the form and never cost a round trip through the model.
 */
/** Try again shortens the conversation; a reply that lands where the failed line was is new and must be spoken. */
export function spokenAfterShrink(spoken: number, previous: number, length: number): number {
  return length < previous ? Math.min(spoken, length - 1) : spoken;
}

export function looksLikeLyrics(text: string): boolean {
  const lines = text.split(/\r?\n/).map((line) => line.trim()).filter(Boolean);
  if (lines.length < 6) return false;
  const short = lines.filter((line) => line.length <= 70 && !/[.!?]\s+\S/.test(line)).length;
  return short / lines.length >= 0.75;
}

const VOICE_PREFERENCE = "caprica.intake.voice";
/** A sleeping sound card or Bluetooth headset swallows the first moment of audio. */
const WARM_UP_MS = 380;
/** Iris says the same welcome every time; it is fetched once per visit. */
const spokenCache = new Map<string, Blob>();
const pause = (ms: number) => new Promise((resolve) => window.setTimeout(resolve, ms));
function readVoicePreference(): boolean {
  try {
    return window.localStorage.getItem(VOICE_PREFERENCE) === "on";
  } catch {
    return false;
  }
}
function writeVoicePreference(on: boolean) {
  try {
    window.localStorage.setItem(VOICE_PREFERENCE, on ? "on" : "off");
  } catch {
    /* A private window simply forgets the choice. */
  }
}

export function IntakeTalk({
  view,
  disabled,
  talk,
  transcribe,
  speak,
  speakStream,
}: {
  view: TalkView;
  disabled: boolean;
  talk: TalkActions;
  transcribe?: (recording: Blob) => Promise<string>;
  /** Iris's voice: one reply as audio. Absent when the Studio cannot speak. */
  speak?: (text: string) => Promise<Blob>;
  /** The same voice, streamed as it is spoken; a 409 falls back to `speak`. */
  speakStream?: (text: string) => Promise<Response>;
}) {
  const [draft, setDraft] = useState("");
  const input = useRef<HTMLTextAreaElement>(null);
  const end = useRef<HTMLDivElement>(null);
  const busy = disabled || view.thinking;
  const level = useRef(0);
  const [voice, setVoice] = useState<VoiceState>("idle");
  const [reveal, setReveal] = useState<{ index: number; words: number } | null>(null);
  const spoken = useRef(-1);
  const seenLength = useRef(0);
  const readBackLink = useRef<HTMLButtonElement>(null);
  const [photoOpen, setPhotoOpen] = useState(false);
  const audio = useRef<HTMLAudioElement>(null);
  const tap = useRef<{ context: AudioContext; analyser: AnalyserNode; routed: boolean; connected: boolean } | null>(null);
  const [voiceOn, setVoiceOn] = useState(() => !!speak && readVoicePreference());
  const [voiceNote, setVoiceNote] = useState("");
  const [speaking, setSpeaking] = useState(false);
  const [replay, setReplay] = useState(0);
  const [reading, setReading] = useState(false);
  /** Stops Iris mid-sentence: the maker may always interrupt. */
  const hush = useRef<() => void>(() => {});
  const voiceOnRef = useRef(voiceOn);
  voiceOnRef.current = voiceOn;

  // The voice runs through one analyser so the iris moves with what is actually heard. It is
  // connected before the first word plays, and only once the audio engine truly runs:
  // switching the output mid-sentence, or through a sleeping engine, loses the first words.
  // `withElement`: the whole-reply voice plays through the <audio> element; a streamed voice
  // is scheduled straight onto the engine and only needs the analyser routed to the speakers.
  const prepareVoice = async (withElement = true): Promise<boolean> => {
    const element = audio.current;
    const Context = typeof window !== "undefined" ? window.AudioContext : undefined;
    if (!element || !Context) return false;
    try {
      if (!tap.current) {
        const context = new Context();
        const analyser = context.createAnalyser();
        analyser.fftSize = 512;
        tap.current = { context, analyser, routed: false, connected: false };
      }
      const current = tap.current;
      if (current.context.state !== "running") await Promise.race([current.context.resume(), pause(500)]);
      if (current.context.state !== "running") return false;
      let fresh = false;
      if (!current.routed) {
        current.analyser.connect(current.context.destination);
        current.routed = true;
        fresh = true;
      }
      if (withElement && !current.connected) {
        current.context.createMediaElementSource(element).connect(current.analyser);
        current.connected = true;
        fresh = true;
      }
      if (fresh) await pause(WARM_UP_MS);
      return true;
    } catch {
      return false;
    }
  };
  const listenToVoice = (): (() => void) => {
    const current = tap.current;
    if (!current?.routed) return () => {};
    try {
      const { analyser } = current;
      const samples = new Uint8Array(analyser.fftSize);
      let frame = 0;
      const read = () => {
        analyser.getByteTimeDomainData(samples);
        let sum = 0;
        for (const sample of samples) sum += ((sample - 128) / 128) ** 2;
        level.current = Math.min(1, Math.sqrt(sum / samples.length) * 5);
        frame = requestAnimationFrame(read);
      };
      frame = requestAnimationFrame(read);
      return () => {
        cancelAnimationFrame(frame);
        level.current = 0;
      };
    } catch {
      return () => {};
    }
  };

  // A new reply appears like a subtitle. With the voice on, the words follow the spoken audio;
  // otherwise the whole line fades in at once.
  useEffect(() => {
    spoken.current = spokenAfterShrink(spoken.current, seenLength.current, view.messages.length);
    seenLength.current = view.messages.length;
    const index = view.messages.length - 1;
    const message = view.messages[index];
    if (!message || message.role !== "caprica" || index <= spoken.current) return;
    spoken.current = index;
    const still = !!window.matchMedia?.("(prefers-reduced-motion: reduce)").matches;
    const words = message.text.split(/(?<=\s)/);
    let cancelled = false;
    let timer = 0;
    let stopListening = () => {};
    let patience = 0;
    let voiceStarted = false;
    let gaveUp = false;
    let streamed: StreamedVoice | null = null;
    let follow = 0;
    const unfold = (pace: (word: string) => number) => {
      let shown = 0;
      const next = () => {
        if (cancelled) return;
        if (shown >= words.length) {
          setReveal(null);
          return;
        }
        const word = words[shown]!;
        shown += 1;
        setReveal({ index, words: shown });
        timer = window.setTimeout(next, pace(word));
      };
      next();
    };
    // Without her voice the whole line fades in at once, like a subtitle (#A); the CSS does the fade.
    const typed = () => setReveal(null);
    const element = audio.current;
    if (voiceOnRef.current && speak && element) {
      setReveal(still ? null : { index, words: 0 });
      // Never a silent, empty bubble: after eight seconds the words appear on their own,
      // and a voice that arrives later simply plays along.
      patience = window.setTimeout(() => {
        if (cancelled || voiceStarted) return;
        gaveUp = true;
        typed();
      }, 8000);
      const began = () => {
        voiceStarted = true;
        window.clearTimeout(patience);
        setSpeaking(true);
        stopListening = listenToVoice();
      };
      const failed = (failure: Error & { status?: number }) => {
        if (cancelled) return;
        setVoiceNote(failure.message || "Iris could not speak that reply.");
        if (failure.status === 409) setVoiceOn(false);
        if (!gaveUp) typed();
      };
      // The whole reply as one recording: OpenAI's voice, or when streaming is not available.
      const speakWhole = () => {
        const cached = spokenCache.get(message.text);
        Promise.all([cached ? Promise.resolve(cached) : speak(message.text), prepareVoice()])
          .then(([recording]) => {
            if (spokenCache.size < 24) spokenCache.set(message.text, recording);
            if (cancelled) return;
            const url = URL.createObjectURL(recording);
            element.src = url;
            element.addEventListener(
              "playing",
              () => {
                if (cancelled) return;
                began();
                if (gaveUp) return; // the words are already on screen
                const total = words.reduce((sum, word) => sum + word.trim().length + 2, 0) || 1;
                const span = (Number.isFinite(element.duration) ? element.duration : words.length * 0.32) * 1000;
                if (still) setReveal(null);
                else unfold((word) => (span * (word.trim().length + 2)) / total);
              },
              { once: true },
            );
            element.addEventListener(
              "ended",
              () => {
                // The cached recording stays valid; only this playback's address is released.
                URL.revokeObjectURL(url);
                stopListening();
                setSpeaking(false);
              },
              { once: true },
            );
            element.play().catch(() => {
              if (!cancelled && !gaveUp) typed();
            });
          })
          .catch(failed);
      };
      if (speakStream) {
        // Streamed: she starts about a second after the reply, while the rest is still being made.
        Promise.all([speakStream(message.text), prepareVoice(false)])
          .then(([response, ready]) => {
            if (cancelled) {
              void response.body?.cancel();
              return;
            }
            if (!ready || !tap.current) throw Object.assign(new Error(""), { fallback: true });
            const voice = playStreamedVoice(response, tap.current.context, tap.current.analyser);
            streamed = voice;
            void voice.started.then(() => {
              if (cancelled) return;
              began();
              if (gaveUp || still) {
                if (still) setReveal(null);
                return;
              }
              // Words follow the voice: the length is estimated until the last piece arrives.
              const total = words.reduce((sum, word) => sum + word.trim().length + 2, 0) || 1;
              const offsets: number[] = [];
              let running = 0;
              for (const word of words) offsets.push((running += word.trim().length + 2) / total);
              const tick = () => {
                if (cancelled) return;
                const span = Math.max(voice.duration(), words.length * 0.3);
                const progress = voice.elapsed() / span;
                const shown = Math.min(words.length, offsets.filter((offset) => offset <= progress + 0.02).length + 1);
                setReveal({ index, words: shown });
                follow = window.setTimeout(tick, 80);
              };
              tick();
            });
            void voice.finished.then(() => {
              window.clearTimeout(follow);
              if (cancelled) return;
              stopListening();
              setSpeaking(false);
              if (!gaveUp) setReveal(null);
            });
          })
          .catch((failure: Error & { status?: number; fallback?: boolean }) => {
            if (cancelled) return;
            // No streaming here (another provider, or the engine is not running): the whole reply.
            if (failure.fallback || failure.status === 409) speakWhole();
            else failed(failure);
          });
      } else speakWhole();
    } else typed();
    const cleanup = () => {
      cancelled = true;
      window.clearTimeout(timer);
      window.clearTimeout(patience);
      window.clearTimeout(follow);
      streamed?.stop();
      stopListening();
      if (element && !element.paused) element.pause();
      level.current = 0;
      setReveal(null);
      setSpeaking(false);
    };
    hush.current = cleanup;
    return cleanup;
    // Only a new message is spoken; directions arriving one by one never interrupt her.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [view.messages.length, replay]);

  const toggleVoice = (force?: boolean) => {
    const on = force ?? !voiceOn;
    if (on === voiceOn) return;
    setVoiceOn(on);
    setVoiceNote("");
    writeVoicePreference(on);
    if (!on) {
      audio.current?.pause();
      setSpeaking(false);
      level.current = 0;
      return;
    }
    // Turning the voice on is the moment the browser allows sound: wake the audio engine now,
    // then say the latest reply.
    void prepareVoice();
    const last = view.messages.length - 1;
    if (view.messages[last]?.role === "caprica") {
      spoken.current = last - 1;
      setReplay((value) => value + 1);
    }
  };

  // A normal conversation: Iris listens by herself between her own turns.
  const irisTurn = disabled || view.thinking || speaking || !!reveal;
  const conversation = useHandsFree({ transcribe, onText: talk.send, paused: irisTurn, level });
  const startConversation = () => {
    void prepareVoice();
    if (speak) toggleVoice(true);
    void conversation.start();
  };
  const hearing = conversation.active && !irisTurn && conversation.phase !== "writing";

  const mode: IrisMode =
    voice === "recording" || hearing
      ? "listening"
      : view.thinking || voice === "listening" || conversation.phase === "writing"
        ? "thinking"
        : reveal || speaking
          ? "speaking"
          : "idle";

  useEffect(() => {
    // Keep Iris's latest words in view by scrolling the screen itself, never the dialog around it.
    const scroller = end.current?.closest(".talk-scroll") as HTMLElement | null | undefined;
    if (scroller) scroller.scrollTop = scroller.scrollHeight;
  }, [view.messages.length, view.thinking, reveal?.words, view.locations.length, view.newCast.length]);
  useEffect(() => {
    // The answer line grows with what is written, up to 40% of the window.
    const field = input.current;
    if (!field) return;
    field.style.height = "auto";
    field.style.height = `${Math.min(field.scrollHeight, Math.round(window.innerHeight * 0.4))}px`;
  }, [draft]);
  useEffect(() => {
    if (!busy) input.current?.focus({ preventScroll: true });
  }, [busy]);

  const send = () => {
    const text = draft.trim();
    if (!text || busy) return;
    talk.send(text);
    setDraft("");
  };
  const needs = view.missing.map((key) => MISSING_LABELS[key] || key);
  // The intake as subtitles (#A): Iris's latest line, a fading tail, one choice under it.
  const screen = subtitleView(view.messages, {
    board: !!view.board,
    castChosen: !!view.castPick?.lead || !!view.castPick?.supporting.length || view.newCast.length > 0,
    canShow: (kind) => kind === "cast" || kind === "wardrobe" ? !!view.castOptions?.length && !!talk.pickCast
      : kind === "photo_session" ? !view.photoChosen && !!talk.photoReady : true,
  });
  const revealingCurrent = !!screen.current && reveal?.index === screen.current.index;
  const { peek: peeking } = useIntakeSource();

  return (
    <div className="intake-talk subtitle-screen">
      <IrisPresence
        mode={mode}
        level={level}
        thinking={!!view.thinking}
        listening={hearing}
        revealing={speaking || !!reveal}
        stop={() => hush.current()}
        corner={
          <>
            {speak && (
              <button
                type="button"
                className="talk-voice"
                aria-pressed={voiceOn}
                title="Iris speaks each reply through your OpenAI connection. Each spoken reply has a small cost."
                onClick={() => toggleVoice()}
              >
                <svg viewBox="0 0 24 24" aria-hidden="true">
                  <path d="M4 10v4h4l5 4V6L8 10H4Z" />
                  {voiceOn ? <path d="M16 9a4 4 0 0 1 0 6M18.5 6.5a7.5 7.5 0 0 1 0 11" /> : <path d="m17 10 4 4m0-4-4 4" />}
                </svg>
                <span>{voiceOn ? "Voice on" : "Voice off"}</span>
                <small>{voiceOn ? "small cost per reply" : "replies stay silent"}</small>
              </button>
            )}
          </>
        }
      />
      {view.progress && !view.board && (
        <p className="talk-progress">
          {!view.board && (
            <span>
              Your story · {view.progress.percent}% · {view.progress.estimate}
            </span>
          )}

        </p>
      )}
      {view.tasteLabel && (
        <p className="talk-taste">
          {view.tasteLabel} ·{" "}
          <button type="button" className="talk-taste-forget" onClick={() => talk.forgetTaste?.()}>
            {icebreakerCopy().forget}
          </button>
        </p>
      )}
      {voiceNote && (
        <p className="talk-voice-note" role="status">
          {voiceNote}
        </p>
      )}
      <audio ref={audio} hidden data-sound-surface="intake-voice" />
      <div className="talk-scroll">
      <SubtitleStage screen={screen} reveal={reveal} thinking={!!view.thinking} />
      <ChoiceSheet screen={screen} view={view} talk={talk} busy={busy} revealing={revealingCurrent} />
      {/* The photo panel opens on the screen, where it can scroll; its chip waits in the quiet row. */}
      {photoOpen && !view.photoChosen && talk.photoReady && (
        <PhotoOffer open castOptions={view.castOptions} disabled={busy} onReady={talk.photoReady} />
      )}


      {!view.board && (view.locations.length > 0 || view.newCast.length > 0 || !!view.wardrobe?.length || !!view.companions?.length) && (
        <div className="talk-captured">
          {view.locations.length > 0 && (
            <section aria-label="Places in story order">
              <h4>Places, in story order</h4>
              <ol className="talk-journey">
                {view.locations.map((place, index) => (
                  <li key={`${place.name}:${index}`}>
                    <b>{place.name}</b>
                    {place.look && <span>{place.look}</span>}
                    {place.story_moment && <small>{place.story_moment}</small>}
                  </li>
                ))}
              </ol>
            </section>
          )}
          {view.newCast.length > 0 && (
            <section aria-label="New characters">
              <h4>New characters to create</h4>
              <ul className="talk-new-cast">
                {view.newCast.map((person) => (
                  <li key={person.name}>
                    <b>{person.name}</b>
                    {person.role && <small>{person.role}</small>}
                    {person.description && <span>{person.description}</span>}
                  </li>
                ))}
              </ul>
              {!view.wardrobe?.length && !view.companions?.length && (
                <p className="talk-fineprint">
                  Nothing is generated yet. After you approve the story, Caprica shows one quote for these
                  characters and places before anything is made.
                </p>
              )}
            </section>
          )}
          {!!view.wardrobe?.length && (
            <section aria-label="Wardrobe">
              <h4>Wardrobe</h4>
              <ul className="talk-new-cast">
                {view.wardrobe.map((dressed) => (
                  <li key={dressed.name}>
                    <b>{dressed.name}</b>
                    {dressed.look ? <small>Saved look · {dressed.look}</small> : dressed.outfit && <span>{dressed.outfit}</span>}
                    {dressed.hair && <span>Hair · {dressed.hair}</span>}
                  </li>
                ))}
              </ul>
            </section>
          )}
          {!!view.companions?.length && (
            <section aria-label="Pets and props">
              <h4>Pets &amp; props</h4>
              <ul className="talk-new-cast">
                {view.companions.map((companion) => (
                  <li key={companion.name}>
                    <b>{companion.name}</b>
                    <small>
                      {companion.kind === "pet" ? "Pet" : "Prop"}
                      {companion.owner ? ` · ${companion.owner}` : ""}
                      {companion.existing ? " · from your library" : " · new"}
                    </small>
                    {companion.description && <span>{companion.description}</span>}
                  </li>
                ))}
              </ul>
            </section>
          )}
          {(!!view.wardrobe?.length || !!view.companions?.length) && (
            <p className="talk-fineprint talk-fineprint-wide">
              Nothing is generated yet. After you approve the story, Caprica shows one quote for everything
              new before anything is made.
            </p>
          )}
        </div>
      )}

      {view.warnings.length > 0 && (
        <ul className="talk-warnings">
          {view.warnings.map((warning) => (
            <li key={warning}>{warning}</li>
          ))}
        </ul>
      )}
      <div ref={end} />
      </div>

      {conversation.active && (
        <div className="talk-live" role="status" data-phase={irisTurn ? "iris" : conversation.phase}>
          <span className="talk-live-dot" aria-hidden="true" />
          <span className="talk-live-text">
            {conversation.phase === "writing"
              ? "Writing down what you said…"
              : view.thinking
                ? "Iris is thinking…"
                : speaking || reveal
                  ? "Iris is speaking — tap her to interrupt"
                  : conversation.phase === "hearing"
                    ? "I'm listening…"
                    : "Just talk — Iris hears when you pause"}
          </span>
          {(speaking || reveal) && (
            <button type="button" className="talk-live-button" onClick={() => hush.current()}>
              Skip
            </button>
          )}
          <button type="button" className="talk-live-button end" onClick={conversation.stop}>
            End conversation
          </button>
        </div>
      )}
      {conversation.message && (
        <p className="talk-voice-note" role="alert">
          {conversation.message}
        </p>
      )}

      <div className="talk-compose">
        <div className="talk-attachments">
          <button type="button" className={`talk-chip${view.song ? " done" : ""}`} disabled={busy} onClick={talk.addSong}>
            {view.song ? `♪ ${view.song}` : "+ Add your song"}
          </button>
          {view.lyrics && <span className="talk-chip done static">{view.lyrics}</span>}
        </div>
        <AnswerLine
          mic={
            <>
                {conversation.supported && !conversation.active && (
                  <button type="button" className="talk-converse" disabled={disabled} onClick={startConversation}>
                    <svg viewBox="0 0 24 24" aria-hidden="true">
                      <path d="M12 3a3 3 0 0 0-3 3v6a3 3 0 0 0 6 0V6a3 3 0 0 0-3-3Zm-7 9a7 7 0 0 0 14 0M12 19v3" />
                    </svg>
                    Talk with Iris
                  </button>
                )}
                {/* One microphone: hands-free talk where the browser can, dictation only where it cannot. */}
                {transcribe && !conversation.active && !conversation.supported && (
                  <VoiceInput
                    transcribe={transcribe}
                    disabled={busy}
                    onLevel={(value) => (level.current = value)}
                    onState={(state) => {
                      setVoice(state);
                      // The maker speaking always interrupts Iris.
                      if (state === "recording") audio.current?.pause();
                    }}
                    onText={(text) => setDraft((current) => [current.trim(), text].filter(Boolean).join(" "))}
                  />
                )}
            </>
          }
          field={
            <textarea
              ref={input}
              id="lyricTalkInput"
              aria-label="Tell Iris about your film"
              rows={Math.min(6, Math.max(1, draft.split("\n").length))}
              value={draft}
              disabled={busy}
              placeholder="Tell me about the song and the film you see — the people, the places, how it ends. You can paste the lyrics here too."
              onChange={(event) => setDraft(event.target.value)}
              onPaste={(event) => {
                // A pasted lyric sheet goes straight to the form; there is nothing to send by hand.
                const pasted = event.clipboardData.getData("text");
                if (busy || draft.trim() || !looksLikeLyrics(pasted)) return;
                event.preventDefault();
                talk.send(pasted);
              }}
              onKeyDown={(event) => {
                if (event.key === "Enter" && !event.shiftKey && !event.nativeEvent.isComposing) {
                  event.preventDefault();
                  send();
                }
              }}
            />
          }
          send={
            <>
                {/* One primary button at a time: Send while talking, the treatment once Iris has enough. */}
                <button type="button" className={view.ready ? "talk-send" : "primary talk-send"} disabled={busy || !draft.trim()} onClick={send}>
                  Send
                </button>
            </>
          }
        />
      </div>

      <div className="talk-footer subtitle-quiet">
        {view.photoChosen ? (
          <p className="talk-photo-ready">📷 {photoCopy().ready} · {view.photoChosen}</p>
        ) : (
          view.photoAvailable &&
          !!view.song &&
          talk.photoReady &&
          view.messages[view.messages.length - 1]?.offer !== "photo_session" && (
            !photoOpen && (
            <button type="button" className="quick-chip photo-offer-chip" disabled={busy} onClick={() => setPhotoOpen(true)}>
              {photoCopy().chip}
            </button>
          )
          )
        )}

        {view.ready && (
          <button type="button" className="talk-write ready" disabled={busy} onClick={talk.write}>
            Write my film treatment
          </button>
        )}
        <span className="talk-status">
          {view.ready ? "Iris has enough to write the treatment." : needs.length && !view.board ? `Still to hear: ${needs.join(", ")}.` : ""}
        </span>
        {talk.peek && (
          <button type="button" className="link talk-peek" aria-expanded={peeking} onClick={() => talk.peek?.()}>
            {peeking ? "Hide what Iris knows" : "See what Iris knows"}
          </button>
        )}
        {screen.castAgain && talk.castAgain && talk.pickCast && !!view.castOptions?.length && (
          <button type="button" className="link talk-cast-again" disabled={busy} onClick={talk.castAgain}>
            {subtitleCopy().castAgain}
          </button>
        )}
        <button ref={readBackLink} type="button" className="link talk-readback" onClick={() => setReading(true)}>
          {subtitleCopy().readBack}
        </button>
        <button type="button" className="link talk-guided" disabled={busy} onClick={talk.guided}>
          I prefer step-by-step questions
        </button>
      </div>
      <ReadBack open={reading} messages={view.messages} close={() => { setReading(false); readBackLink.current?.focus(); }} />
    </div>
  );
}
